import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Package,
  FileText,
  ShoppingCart,
  Receipt,
  ArrowLeftRight,
  BarChart3,
  Settings,
  Users,
  Building2,
  Truck,
  UserCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react';

import { useAuth } from '../../providers/AuthProvider';
import type { Permission } from '../../types/auth';

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  permission?: Permission;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'navigation.dashboard', href: '/', icon: LayoutDashboard },
  { name: 'navigation.products', href: '/products', icon: Package, permission: 'items:read' },
  {
    name: 'navigation.invoices',
    href: '/invoices',
    icon: FileText,
    permission: 'invoices:read',
    children: [
      { name: 'navigation.purchases', href: '/invoices/purchases', icon: ShoppingCart },
      { name: 'navigation.sales', href: '/invoices/sales', icon: Receipt },
      { name: 'navigation.transfers', href: '/invoices/transfers', icon: ArrowLeftRight },
    ],
  },
  { name: 'navigation.reports', href: '/reports', icon: BarChart3, permission: 'reports:read' },
  { name: 'navigation.suppliers', href: '/suppliers', icon: Truck, permission: 'items:read' },
  { name: 'navigation.customers', href: '/customers', icon: UserCircle, permission: 'invoices:read' },
  { name: 'navigation.outlets', href: '/outlets', icon: Building2, permission: 'settings:read' },
  { name: 'navigation.users', href: '/admin/users', icon: Users, permission: 'users:read' },
  { name: 'navigation.settings', href: '/settings', icon: Settings, permission: 'settings:read' },
];

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const isRTL = i18n.dir() === 'rtl';
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  const toggleExpanded = (name: string) => {
    setExpandedItems((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };

  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const filteredNavigation = navigation.filter((item) => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 z-30 flex w-64 flex-col bg-white border-secondary-200
          transition-transform duration-300 ease-in-out
          ltr:left-0 ltr:border-r rtl:right-0 rtl:border-l
          ${isOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full'}
          lg:!translate-x-0 lg:static lg:z-auto
        `}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-secondary-200">
          <h1 className="text-xl font-bold text-primary-600">
            {t('appName')}
          </h1>
          <button
            type="button"
            onClick={onToggle}
            className="lg:hidden p-2 rounded-md text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100"
            aria-label={t('close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = isActiveRoute(item.href);
              const isExpanded = expandedItems.includes(item.name);
              const hasChildren = item.children && item.children.length > 0;
              const Icon = item.icon;

              return (
                <li key={item.name}>
                  {hasChildren ? (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(item.name)}
                        className={`
                          w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium
                          transition-colors duration-150
                          ${isActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-secondary-700 hover:bg-secondary-50 hover:text-secondary-900'
                          }
                        `}
                      >
                        <span className="flex items-center">
                          <Icon className="h-5 w-5 ltr:mr-3 rtl:ml-3 flex-shrink-0" />
                          {t(item.name)}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {isExpanded && (
                        <ul className="mt-1 space-y-1 ltr:pl-8 rtl:pr-8">
                          {item.children?.map((child) => {
                            const ChildIcon = child.icon;
                            const isChildActive = isActiveRoute(child.href);

                            return (
                              <li key={child.name}>
                                <NavLink
                                  to={child.href}
                                  className={`
                                    flex items-center px-3 py-2 rounded-md text-sm font-medium
                                    transition-colors duration-150
                                    ${isChildActive
                                      ? 'bg-primary-50 text-primary-700'
                                      : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                                    }
                                  `}
                                >
                                  <ChildIcon className="h-4 w-4 ltr:mr-3 rtl:ml-3 flex-shrink-0" />
                                  {t(child.name)}
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={item.href}
                      className={`
                        flex items-center px-3 py-2 rounded-md text-sm font-medium
                        transition-colors duration-150
                        ${isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-secondary-700 hover:bg-secondary-50 hover:text-secondary-900'
                        }
                      `}
                    >
                      <Icon className="h-5 w-5 ltr:mr-3 rtl:ml-3 flex-shrink-0" />
                      {t(item.name)}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Sidebar footer - collapse button for large screens */}
        <div className="hidden lg:flex border-t border-secondary-200 p-3">
          <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900"
          >
            <ChevronIcon className="h-5 w-5" />
          </button>
        </div>
      </aside>

      {/* Mobile menu button */}
      <button
        type="button"
        onClick={onToggle}
        className="fixed bottom-4 z-40 p-3 rounded-full bg-primary-600 text-white shadow-lg lg:hidden ltr:left-4 rtl:right-4"
        aria-label={t('open')}
      >
        <Menu className="h-6 w-6" />
      </button>
    </>
  );
}
